import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useFeatureFlags, Module } from '../contexts/FeatureFlagContext';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import axios from 'axios';
import MfaSettings from './auth/MfaSettings';
import { motion } from 'framer-motion';
import { useRole } from '../hooks/useRole';
import {
  Settings,
  LayoutGrid,
  ShieldCheck,
  RefreshCcw,
  Save,
  CheckCircle2,
  BarChart3,
  Bell,
  Fingerprint,
  User as UserIcon,
  Monitor,
  Lock,
  CheckSquare,
  Square,
  Building2,
  Users
} from 'lucide-react';
import { cn } from '../lib/utils';

import { DateField } from './shared/DateField';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1.125rem;
    font-weight: 700;
    color: white;
    margin: 0;
  }
`;

const UserPreferencesScreen: React.FC = () => {
  const { preferences, updatePreferences } = useUserPreferences();
  const { userRole } = useRole();
  // F-CMP-016: the navigation workspace is a personal setting; every authenticated role may edit its own.
  // (`canEdit` from useRole is a data-edit permission — ADMIN/TRADER/PM — and had locked CCOs and risk out of their own workspace.)
  const canEdit = true;
  const { isModuleEnabled } = useFeatureFlags();
  const { isInstitutional, isRetail, clientType } = useTenantConfig();
  const [enabledTabs, setEnabledTabs] = useState<string[]>(preferences?.enabledTabs || []);
  const [defaultFilters, setDefaultFilters] = useState<Record<string, any>>(preferences?.defaultBlotterFilters || {});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (preferences?.enabledTabs) {
      setEnabledTabs(preferences.enabledTabs);
    }
    if (preferences?.defaultBlotterFilters) {
      setDefaultFilters(preferences.defaultBlotterFilters);
    }
  }, [preferences]);

  // All available tabs - some are module-gated, some are client-type specific
  // Note: For Retail clients, Households/Models/Onboarding are accessed via Reference tab
  // Fee Billing is accessed via Middle Office, Sleeve/UMA via Portfolios
  const allTabsConfig = [
    // Core tabs (all clients)
    { id: '/home', label: 'Home', desc: 'Main performance and risk overview', category: 'core' },
    { id: '/bond-transactions', label: 'Blotter', desc: 'Real-time order and execution tracking', category: 'core' },
    { id: '/trade-entry/fixed-income', label: 'Trade Entry', desc: 'Standard bond trade execution', category: 'core' },
    { id: '/portfolio-management', label: 'Portfolios', desc: isRetail ? 'Holdings, model trading, drift, and Sleeve/UMA' : 'Holdings and rebalancing engine', category: 'core' },
    { id: '/ibor', label: 'IBOR', desc: 'Investment Book of Record', category: 'core' },  // Core module - always available
    { id: '/analytics', label: 'Analytics', desc: isRetail ? 'Portfolio health, performance, and client statements' : 'Risk and attribution models', module: isRetail ? undefined : Module.RISK_ANALYTICS, category: 'core' },
    { id: '/ai-agents', label: 'AI Agents', desc: 'Agentic workflow assistance', category: 'ai' },
    { id: '/middle-office', label: 'Middle Office', desc: isRetail ? 'Cash, reconciliation, statements, and fee billing' : 'Settlement and reconciliation', module: Module.MIDDLE_OFFICE, category: 'core' },
    { id: '/market-data', label: 'Market', desc: 'Yield curves and credit spreads', category: 'core' },
    { id: '/reference-data', label: 'Reference', desc: isRetail ? 'Clients, households, models, securities, and compliance' : 'Portfolios, groups, securities, and compliance', category: 'core' },
    // Note: Retail clients use Analytics tab which contains Portfolio Health, Performance, and Statements
    // No separate retail-only tabs needed here - Analytics covers retail analytics needs
    // Institutional-specific tabs
    ...(isInstitutional ? [
      { id: '/compliance-dashboard', label: 'Compliance', desc: 'What-if scenarios, pre-clear queue, overrides, regulatory rules', category: 'core' },
      { id: '/insurance-alm', label: 'Insurance ALM', desc: 'Duration gap, KRD mismatch, cash flow projection, liability ingestion', category: 'core' },
      { id: '/insurance-accounting', label: 'Insurance Accounting', desc: 'Book yield, RBC capital, IMR/AVR, NAIC schedules, capital efficiency', category: 'core' },
      { id: '/autonomous-agents', label: 'Autonomous Agents', desc: 'AI-driven autonomous operations', category: 'ai' },
      { id: '/command-center', label: 'Command Center', desc: 'Natural language trade execution', category: 'ai' },
    ] : []),
  ];

  // Filter tabs based on licensed modules
  const allTabs = useMemo(() => {
    return allTabsConfig.filter(tab => {
      // If tab has a module requirement, check if it's enabled
      if (tab.module && !isModuleEnabled(tab.module)) {
        return false;
      }
      return true;
    });
  }, [isModuleEnabled]);

  const handleToggleTab = (tabId: string) => {
    if (!canEdit) return;
    setEnabledTabs(prev => 
      prev.includes(tabId) ? prev.filter(id => id !== tabId) : [...prev, tabId]
    );
  };

  const handleSelectAll = () => {
    if (!canEdit) return;
    setEnabledTabs(allTabs.map(t => t.id));
  };

  const handleClearAll = () => {
    if (!canEdit) return;
    setEnabledTabs([]);
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await updatePreferences({ ...preferences, enabledTabs, defaultBlotterFilters: defaultFilters });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to save preferences:', error);
      setSaveError(error.response?.data?.error || error.message || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container>
      <div className="max-w-[1200px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Settings className="w-8 h-8 text-blue-500" />
                User Preferences
                <span className={cn(
                  "ml-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border",
                  isRetail
                    ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                    : "bg-blue-500/10 border-blue-500/20 text-blue-400"
                )}>
                  {isRetail ? 'RIA' : 'Institutional'}
                </span>
              </h1>
              <p className="text-slate-500 font-medium mt-1">
                Configure your personal workspace and security settings for {isRetail ? 'wealth management' : 'institutional trading'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {saveError && <span className="text-xs text-red-400 font-medium">{saveError}</span>}
              <button 
                onClick={handleSave}
                disabled={isSaving || !canEdit}
                className={cn(
                  "flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg",
                  saveSuccess 
                    ? "bg-emerald-600 text-white shadow-emerald-600/20" 
                    : !canEdit
                      ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                      : "bg-blue-600 text-white shadow-blue-600/20 hover:bg-blue-500 disabled:opacity-50"
                )}
              >
                {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : 
                 saveSuccess ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {saveSuccess ? 'Preferences Saved' : 'Save Changes'}
              </button>
            </div>
          </div>

          {!canEdit && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center gap-3 text-amber-400 text-sm font-medium">
              <Lock className="w-4 h-4" />
              You are currently in read-only mode. Your institutional role ({userRole}) restricts configuration changes to administrators or authorized personnel.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-8">
              <GlassCard className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <SectionHeader>
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20">
                      <LayoutGrid className="w-5 h-5" />
                    </div>
                    <div>
                      <h3>Navigation Workspace</h3>
                      <p className="text-xs text-slate-500 font-medium">Select which modules appear in your main menu</p>
                    </div>
                  </SectionHeader>
                  
                  {canEdit && (
                    <div className="flex gap-2">
                      <button 
                        onClick={handleSelectAll}
                        className="text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-widest bg-blue-500/10 px-2 py-1 rounded-md"
                      >
                        Select All
                      </button>
                      <button 
                        onClick={handleClearAll}
                        className="text-[10px] font-bold text-slate-500 hover:text-slate-400 transition-colors uppercase tracking-widest bg-white/5 px-2 py-1 rounded-md"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  {allTabs.map(tab => (
                    <div 
                      key={tab.id}
                      onClick={() => handleToggleTab(tab.id)}
                      className={cn(
                        "flex items-center justify-between p-4 rounded-xl transition-all border",
                        canEdit ? "cursor-pointer" : "cursor-not-allowed",
                        enabledTabs.includes(tab.id) 
                          ? "bg-blue-500/10 border-blue-500/30 shadow-inner" 
                          : "bg-slate-950/30 border-white/5 hover:border-white/10 hover:bg-white/5"
                      )}
                    >
                      <div className="flex flex-col">
                        <span className={cn("text-sm font-bold transition-colors", enabledTabs.includes(tab.id) ? "text-white" : "text-slate-400")}>{tab.label}</span>
                        <span className="text-[10px] text-slate-500 mt-0.5">{tab.desc}</span>
                      </div>
                      <div className={cn(
                        "w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all",
                        enabledTabs.includes(tab.id) ? "bg-blue-500 border-blue-500" : "border-slate-700 bg-transparent"
                      )}>
                        {enabledTabs.includes(tab.id) && <CheckCircle2 className="w-3.5 h-3.5 text-white stroke-[3]" />}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>

              <GlassCard className="p-8">
                <SectionHeader>
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-400 border border-amber-500/20">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3>Default Blotter Filters</h3>
                    <p className="text-xs text-slate-500 font-medium">Set defaults applied when opening the Trade Blotter</p>
                  </div>
                </SectionHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 font-bold uppercase tracking-wider mb-1.5">Status</label>
                      <select
                        value={defaultFilters.status || ''}
                        onChange={(e) => setDefaultFilters({ ...defaultFilters, status: e.target.value || undefined })}
                        disabled={!canEdit}
                        className="w-full bg-slate-950/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-300 focus:border-blue-500/50 focus:outline-none disabled:opacity-50"
                      >
                        <option value="">All Statuses</option>
                        <option value="DRAFT">Draft</option>
                        <option value="SENT">Sent</option>
                        <option value="EXECUTED">Executed</option>
                        <option value="POSTED">Posted</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 font-bold uppercase tracking-wider mb-1.5">Type</label>
                      <select
                        value={defaultFilters.transactionType || ''}
                        onChange={(e) => setDefaultFilters({ ...defaultFilters, transactionType: e.target.value || undefined })}
                        disabled={!canEdit}
                        className="w-full bg-slate-950/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-300 focus:border-blue-500/50 focus:outline-none disabled:opacity-50"
                      >
                        <option value="">All Types</option>
                        <option value="BUY">Buy</option>
                        <option value="SELL">Sell</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 font-bold uppercase tracking-wider mb-1.5">Date From</label>
                      <DateField
                        value={defaultFilters.tradeDateFrom || ''}
                        disabled={!canEdit}
                        className="w-full"
                        onChange={iso => setDefaultFilters({ ...defaultFilters, tradeDateFrom: iso || undefined })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 font-bold uppercase tracking-wider mb-1.5">Date To</label>
                      <DateField
                        value={defaultFilters.tradeDateTo || ''}
                        disabled={!canEdit}
                        className="w-full"
                        onChange={iso => setDefaultFilters({ ...defaultFilters, tradeDateTo: iso || undefined })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 font-bold uppercase tracking-wider mb-1.5">Portfolio</label>
                    <input
                      type="text"
                      placeholder="Leave empty for all portfolios"
                      value={defaultFilters.portfolio || ''}
                      onChange={(e) => setDefaultFilters({ ...defaultFilters, portfolio: e.target.value || undefined })}
                      disabled={!canEdit}
                      className="w-full bg-slate-950/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-300 placeholder:text-slate-600 focus:border-blue-500/50 focus:outline-none disabled:opacity-50"
                    />
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-8">
                <SectionHeader>
                  <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 border border-violet-500/20">
                    <Monitor className="w-5 h-5" />
                  </div>
                  <div>
                    <h3>Display Settings</h3>
                    <p className="text-xs text-slate-500 font-medium">Customize your visual interface experience</p>
                  </div>
                </SectionHeader>
                <div className="space-y-4">
                  {[
                    { label: 'High-Density Grids', value: 'Enabled' },
                    { label: 'Real-time Animations', value: 'Smooth' },
                    { label: 'Default Theme', value: 'Axiom Dark (Slate)' }
                  ].map(s => (
                    <div key={s.label} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                      <span className="text-sm text-slate-400 font-medium">{s.label}</span>
                      <span className="text-sm text-blue-400 font-bold">{s.value}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </div>

            <div className="space-y-8">
              <GlassCard className="p-8">
                <SectionHeader>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20">
                    <Fingerprint className="w-5 h-5" />
                  </div>
                  <div>
                    <h3>Security & Access</h3>
                    <p className="text-xs text-slate-500 font-medium">Manage Multi-Factor Authentication</p>
                  </div>
                </SectionHeader>
                <div className="bg-slate-950/50 rounded-2xl border border-white/5">
                  <MfaSettings />
                </div>
              </GlassCard>

              <GlassCard className={cn(
                "p-8 bg-gradient-to-br border",
                isRetail
                  ? "from-emerald-600/10 to-teal-600/10 border-emerald-500/20"
                  : "from-blue-600/10 to-violet-600/10 border-blue-500/20"
              )}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center border border-white/10">
                    {isRetail ? <Users className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white">
                      {isRetail ? 'RIA Profile' : 'Institutional Profile'}
                    </h4>
                    <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">
                      {isRetail ? 'Wealth Management' : 'Standard Tier Account'}
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5 space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Client Type</span>
                    <span className={cn(
                      "font-bold",
                      isRetail ? "text-emerald-400" : "text-blue-400"
                    )}>
                      {clientType || (isRetail ? 'RETAIL' : 'INSTITUTIONAL')}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Session ID</span>
                    <span className="text-slate-300 font-mono">AX-9928-XM</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Last Login</span>
                    <span className="text-slate-300">Jan 10, 2026 23:14 EST</span>
                  </div>
                  {isRetail && (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Enabled Features</span>
                        <span className="text-emerald-400">Households, Models, Fee Billing</span>
                      </div>
                    </>
                  )}
                </div>
              </GlassCard>
            </div>
          </div>
        </motion.div>
      </div>
    </Container>
  );
};

export default UserPreferencesScreen;
